import React, { FC, useState } from 'react';
import styles from './styles.module.css';
import { Asset as SharedAsset } from 'Shared/components';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from 'Shared/components';
import cx from 'classnames';
import { ActivityType } from '@clearcover/core-app-types';
import {
  formatDateFromUTCShort,
  formatMoneyWithNegativeParenthesis,
  isPresent,
} from 'Shared/utils';
import {
  Asset,
  ErrorIcon,
  YesIcon,
  SwapHorizontalIcon,
} from '@clearcover/atlas';
import { paymentIcon } from 'Shared/constants/paymentIcon';
import { usePolicyContext } from 'Policies/utils';

type PolicyActivitiesPropsType = {
  activities: Array<ActivityType>;
};

const PaymentBreakdown: FC<{
  breakdown: Array<{ name: string; value: number }>;
}> = ({ breakdown }) => {
  return (
    <div className={styles.paymentBreakdown}>
      {breakdown.map((item, index) => (
        <div key={index} className={styles.paymentBreakdownItem}>
          <div></div>
          <div>{item.name}</div>
          <div>{formatMoneyWithNegativeParenthesis(item.value)}</div>
        </div>
      ))}
    </div>
  );
};

const DisplayActivity: FC<{
  activity: ActivityType;
  child: boolean;
  timeZone?: string;
}> = ({ activity, timeZone, child }) => {
  const [isOpen, toggleOpen] = useState(false);

  return (
    <>
      <li
        key={`activity-${activity.id}`}
        data-testid={`activity-${activity.id}`}
        className={cx(styles.activity, { [styles.child]: child })}
      >
        <div
          className={styles.data}
          data-testid={`activity-date-${activity.id}`}
        >
          {formatDateFromUTCShort(activity.date, timeZone)}
        </div>
        <div
          className={cx(styles.data, styles.description)}
          data-testid={`activity-description-${activity.id}`}
        >
          {activity.type === 'CHARGE_BACK' && (
            <Asset
              icon={SwapHorizontalIcon}
              height={24}
              width={24}
              description="Charge Back"
            />
          )}
          {activity.type === 'CHARGE_BACK_REVERSAL' && (
            <SharedAsset variant="reversed" />
          )}
          {activity.description}
        </div>
        <div
          className={styles.data}
          data-testid={`activity-amount-${activity.id}`}
        >
          {formatMoneyWithNegativeParenthesis(activity.amount.value, {
            formatWithSymbol: true,
          })}
        </div>
        <div
          className={styles.data}
          data-testid={`activity-source-${activity.id}`}
        >
          {activity.actor.type}
        </div>
        <div
          className={styles.data}
          data-testid={`activity-payment-method-${activity.id}`}
        >
          {activity.paymentMethod ? (
            <div className={cx(styles.value, styles.paymentMethod)}>
              {activity.paymentMethod.type !== 'bank' && (
                <Asset
                  icon={paymentIcon[activity.paymentMethod.cardType ?? 'other']}
                  description="credit card"
                  height={12}
                  width={12}
                />
              )}
              {activity.paymentMethod.lastFour
                ? `•••• ${activity.paymentMethod.lastFour}`
                : 'None'}
            </div>
          ) : (
            ''
          )}
        </div>
        <div
          className={cx(styles.data, styles.status)}
          data-testid={`activity-status-${activity.id}`}
        >
          <div
            className={cx(styles.value, styles.status, {
              [styles.failed]: activity.status === 'Failed',
              [styles.chargeback]: activity.type === 'CHARGE_BACK',
            })}
          >
            {activity.status === 'Success' && (
              <Asset
                description="Success"
                icon={YesIcon}
                height={16}
                width={16}
              />
            )}
            {activity.status === 'Failed' && (
              <Asset
                description="Failed"
                icon={ErrorIcon}
                height={16}
                width={16}
              />
            )}
            {activity.statusReason || activity.status}
          </div>
        </div>
        <div
          className={cx(styles.transactionDetailsToggle)}
          onClick={() => toggleOpen(!isOpen)}
        >
          {isPresent(activity.transactionDetails) && (
            <motion.div
              initial={false}
              exit="exit"
              animate={{
                transition: { ease: 'easeIn' },
                rotate: isOpen ? 180 : 0,
              }}
            >
              <SharedAsset variant="upArrow" />
            </motion.div>
          )}
        </div>
      </li>
      {isPresent(activity.transactionDetails) &&
        activity.transactionDetails.breakdown.length > 0 && (
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key="content"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: {
                    opacity: 1,
                    height: 'auto',
                    transition: { ease: 'easeIn' },
                  },
                  collapsed: {
                    opacity: 0,
                    height: 0,
                    transition: { ease: 'easeOut' },
                  },
                }}
              >
                <PaymentBreakdown
                  breakdown={activity.transactionDetails.breakdown}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
    </>
  );
};

export const PolicyBillingActivities: FC<PolicyActivitiesPropsType> = ({
  activities,
}) => {
  const {
    details: { timeZone },
  } = usePolicyContext();
  if (activities.length === 0) return null;
  return (
    <Card id={'billing-activities-card'} title={'Billing Activities'}>
      <ul className={styles.list}>
        <li className={styles.headers}>
          <span className={styles.header}>Date</span>
          <span className={styles.header}>Description</span>
          <span className={styles.header}>Amount</span>
          <span className={styles.header}>Source</span>
          <span className={styles.header}>Payment Method</span>
          <span className={styles.header}>Payment Status</span>
        </li>
        {activities.map(activity => (
          <div key={activity.id} className={styles.activityContainer}>
            <DisplayActivity
              activity={activity}
              timeZone={timeZone}
              child={false}
            />

            {isPresent(activity.children)
              ? activity.children.map(childActivity => (
                  <DisplayActivity
                    activity={childActivity}
                    timeZone={timeZone}
                    child={true}
                  />
                ))
              : null}
          </div>
        ))}
      </ul>
    </Card>
  );
};
